import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
    X, Send, ArrowUpRight, Sparkles, MapPin, FileText, Play, BookOpen,
    Calculator, LayoutGrid, GraduationCap, TrainFront, TrendingUp, DollarSign,
    Heart, Home, Check, CalendarCheck, Bell, ArrowRight,
} from 'lucide-react';
import { TID } from '../../constants/testIds';
import { API_BASE } from '../../lib/api';

function newSessionId() {
    return 'bn_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

// -----------------------------------------------------------------------------
// Page-context inference — Ask Sam adapts its opening posture based on where the
// visitor is. Runs cheaply on every render via react-router.
// -----------------------------------------------------------------------------
function inferPageContext(pathname, routeParams) {
    const p = pathname || '/';
    // Neighbourhood detail: /neighbourhoods/:slug (excluding /report)
    const nMatch = p.match(/^\/neighbourhoods\/([^/]+)(?:\/report)?$/);
    if (nMatch && !p.endsWith('/report')) {
        return {
            path: p,
            page_type: 'neighbourhood_detail',
            neighbourhood_slug: nMatch[1],
            neighbourhood_name: nMatch[1].replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        };
    }
    if (p === '/investor-centre' || p.startsWith('/investor-centre') || p.startsWith('/investors/')) {
        return { path: p, page_type: 'investor_centre' };
    }
    if (p === '/seller-centre' || p.startsWith('/seller-centre') || p.startsWith('/sellers/')) {
        return { path: p, page_type: 'seller_centre' };
    }
    if (p === '/buyer-centre' || p.startsWith('/buyer-centre')) {
        return { path: p, page_type: 'buyer_centre' };
    }
    if (p === '/match' || p === '/quiz') return { path: p, page_type: 'match_assessment' };
    if (p === '/affordability') return { path: p, page_type: 'affordability_calculator' };
    if (p === '/invest') return { path: p, page_type: 'investment_terminal' };
    if (p === '/compare') return { path: p, page_type: 'neighbourhood_compare' };
    if (p.startsWith('/videos')) return { path: p, page_type: 'video_centre' };
    if (p.startsWith('/insights')) return { path: p, page_type: 'insights' };
    if (p === '/') return { path: p, page_type: 'home' };
    return { path: p, page_type: 'generic' };
}

// -----------------------------------------------------------------------------
// Starter prompts. Rendered on the welcome screen; the LLM never sees these.
// -----------------------------------------------------------------------------
const STARTERS_DEFAULT = [
    { icon: MapPin,      q: 'Where should I buy if I work in downtown Toronto?' },
    { icon: Home,        q: 'Compare Ancaster vs Oakville' },
    { icon: Heart,       q: 'Best neighbourhoods for young families' },
    { icon: TrendingUp,  q: 'Best investment areas under $900,000' },
    { icon: GraduationCap, q: 'Which areas have the best school districts?' },
    { icon: TrainFront,  q: 'Compare Burlington vs Milton' },
];

// Context-aware starter overrides — replaces the neutral starters based on page.
function startersFor(ctx) {
    if (ctx.page_type === 'neighbourhood_detail') {
        const nm = ctx.neighbourhood_name || 'this neighbourhood';
        return [
            { icon: MapPin, q: `Tell me the honest trade-offs of living in ${nm}` },
            { icon: GraduationCap, q: `What are the schools like in ${nm}?` },
            { icon: TrainFront, q: `What is the commute from ${nm} to downtown Toronto?` },
            { icon: TrendingUp, q: `How has ${nm} appreciated over the last five years?` },
            { icon: Home, q: `Which nearby neighbourhoods should I compare with ${nm}?` },
            { icon: DollarSign, q: `Is ${nm} a good investment right now?` },
        ];
    }
    if (ctx.page_type === 'investor_centre') {
        return [
            { icon: TrendingUp, q: 'Which Southern Ontario neighbourhoods have the best 5-year appreciation outlook?' },
            { icon: DollarSign, q: 'Best investment areas under $900,000' },
            { icon: Home, q: 'What is a realistic cash-flow-positive rental in Hamilton?' },
            { icon: Calculator, q: 'How should I think about cap rate vs appreciation?' },
        ];
    }
    if (ctx.page_type === 'buyer_centre' || ctx.page_type === 'match_assessment' || ctx.page_type === 'affordability_calculator') {
        return [
            { icon: DollarSign, q: 'I earn $150k household. What is realistic for me in Southern Ontario?' },
            { icon: GraduationCap, q: 'Best neighbourhoods for young families' },
            { icon: TrainFront, q: 'Where should I buy if I work in downtown Toronto?' },
            { icon: Home, q: 'Compare Burlington vs Milton' },
        ];
    }
    return STARTERS_DEFAULT;
}

// -----------------------------------------------------------------------------
// Contextual welcome intros. The full welcome COPY (below) is always shown;
// this is just the two-line lead-in that adapts to the page.
// -----------------------------------------------------------------------------
function welcomeLeadFor(ctx) {
    switch (ctx.page_type) {
        case 'neighbourhood_detail':
            return `I see you're looking at ${ctx.neighbourhood_name || 'this neighbourhood'}. Ask me anything about it — schools, price trends, commute, or how it stacks up against a comparable option.`;
        case 'investor_centre':
            return "I'm your investment lens on Southern Ontario. Ask me about cash flow, appreciation, or where the smart money is looking right now.";
        case 'seller_centre':
            return "I can walk you through pricing, prep, timing and negotiation. What's on your mind about selling?";
        case 'buyer_centre':
            return "I'm here to help you buy well. From budget to shortlist to offer strategy — where would you like to start?";
        case 'match_assessment':
            return "Working through the Match Assessment? I can help you interpret results or explore any neighbourhood in more depth.";
        case 'affordability_calculator':
            return "Numbers can only tell part of the story. Ask me anything about what your budget really buys across Southern Ontario.";
        case 'investment_terminal':
            return "I can help you read the terminal — appreciation drivers, cash flow ranges, or which neighbourhoods deserve a closer look.";
        default:
            return null;
    }
}

// -----------------------------------------------------------------------------
// Recommendation card metadata.
// -----------------------------------------------------------------------------
const SESSION_URL = {
    buyer: '/strategy-session',
    listing: '/sellers/consultation',
    investor: '/investors/strategy-session',
};

const REC_META = {
    neighbourhood: { icon: MapPin,   url: (s) => `/neighbourhoods/${s}`,          label: 'Neighbourhood' },
    report:        { icon: FileText, url: (s) => `/neighbourhoods/${s}/report`,   label: 'Intelligence Report' },
    video:         { icon: Play,     url: (s) => `/videos/${s}`,                  label: 'Video' },
    article:       { icon: BookOpen, url: (s) => `/insights/${s}`,                label: 'Editorial' },
    calculator: {
        icon: Calculator,
        url: (s) => ({
            affordability: '/affordability',
            match: '/match',
            invest: '/invest',
            'cash-flow': '/investor-centre#calculators',
            roi: '/investor-centre#calculators',
            'cap-rate': '/investor-centre#calculators',
        }[s] || '/affordability'),
        label: 'Tool',
    },
    hub: {
        icon: LayoutGrid,
        url: (s) => ({
            'buyer-centre': '/buyer-centre',
            'seller-centre': '/seller-centre',
            'investor-centre': '/investor-centre',
            videos: '/videos',
            insights: '/insights',
        }[s] || '/'),
        label: 'Hub',
    },
    region: { icon: MapPin, url: (s) => `/${s}`, label: 'Region' },
};

function parseRecommendations(raw) {
    if (!raw) return { display: '', recs: [], cta: null };
    const m = raw.match(/<RECOMMENDATIONS>([\s\S]*?)(<\/RECOMMENDATIONS>|$)/);
    if (!m) return { display: raw, recs: [], cta: null };
    const display = raw.slice(0, m.index).replace(/\s+$/, '');
    let recs = [];
    let cta = null;
    try {
        const parsed = JSON.parse(m[1].trim());
        recs = Array.isArray(parsed?.cards)
            ? parsed.cards.filter((c) => c && c.type && c.slug && REC_META[c.type])
            : [];
        if (parsed?.cta && parsed.cta.show === true) {
            const kind = parsed.cta.kind || 'buyer';
            cta = { kind, url: SESSION_URL[kind] || SESSION_URL.buyer, note: parsed.cta.note || '' };
        }
    } catch (_e) { /* partial stream */ }
    return { display, recs, cta };
}

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------
export default function ChatAdvisor() {
    const location = useLocation();
    const routeParams = useParams();
    const pageContext = useMemo(
        () => inferPageContext(location.pathname, routeParams),
        [location.pathname, routeParams],
    );

    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [handoff, setHandoff] = useState(null); // {checklist, ready_for_handoff, ...}
    const [subscribeOpen, setSubscribeOpen] = useState(false);

    const sessionRef = useRef(null);
    const scrollRef = useRef(null);

    if (!sessionRef.current) sessionRef.current = newSessionId();

    // Fresh contextual welcome the first time the panel opens.
    useEffect(() => {
        if (open && messages.length === 0) {
            const lead = welcomeLeadFor(pageContext);
            setMessages([{ role: 'assistant', kind: 'welcome', lead }]);
        }
    }, [open, messages.length, pageContext]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: 9e9, behavior: 'smooth' });
    }, [messages, open, handoff, subscribeOpen]);

    // Poll the handoff endpoint after every assistant turn to update the profile summary.
    async function refreshHandoff() {
        try {
            const r = await fetch(`${API_BASE}/advisor/handoff/${sessionRef.current}`);
            if (!r.ok) return;
            const j = await r.json();
            setHandoff(j);
        } catch (_e) { /* soft-fail */ }
    }

    async function ask(userText) {
        const trimmed = userText.trim();
        if (!trimmed || busy) return;
        setMessages((m) => [...m, { role: 'user', text: trimmed }, { role: 'assistant', text: '' }]);
        setText('');
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/advisor/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    session_id: sessionRef.current,
                    text: trimmed,
                    page_context: pageContext,
                }),
            });
            if (!res.ok || !res.body) throw new Error('stream failed');
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = '';
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const parts = buf.split('\n\n');
                buf = parts.pop() || '';
                for (const p of parts) {
                    if (!p.startsWith('data:')) continue;
                    try {
                        const j = JSON.parse(p.slice(5).trim());
                        if (j.delta) {
                            setMessages((m) => {
                                const copy = [...m];
                                const last = copy[copy.length - 1];
                                if (last && last.role === 'assistant') {
                                    // Immutable update — React 18 strict-mode double-invokes
                                    // the updater; mutating `last.text` would double-append.
                                    copy[copy.length - 1] = { ...last, text: (last.text || '') + j.delta };
                                }
                                return copy;
                            });
                        }
                        if (j.done) break;
                    } catch (_e) { /* partial */ }
                }
            }
        } catch (_e) {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: 'assistant', text: 'Ask Sam is temporarily unavailable. Please try again in a moment.' };
                return copy;
            });
        } finally {
            setBusy(false);
            refreshHandoff();
        }
    }

    const showWelcome = messages.length <= 1 && messages[0]?.kind === 'welcome';
    const starters = useMemo(() => startersFor(pageContext), [pageContext]);

    return (
        <>
            {/* ---- Floating "✨ Ask Sam" button ---- */}
            {!open && (
                <button
                    data-testid={TID.chat.toggle}
                    onClick={() => setOpen(true)}
                    className="fixed z-40 bottom-6 right-6 group inline-flex items-center gap-2.5 pl-4 pr-5 py-3 text-[14px] font-medium text-white bg-[#0B1F36] hover:bg-[#0F2A47] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(11,31,54,0.35)] shadow-[0_10px_28px_rgba(11,31,54,0.28)]"
                    style={{ borderRadius: 9999, border: '1px solid rgba(200,162,74,0.55)' }}
                >
                    <span className="w-6 h-6 inline-flex items-center justify-center bg-[#C8A24A] text-[#0B1F36] rounded-full transition-transform duration-300 group-hover:rotate-12">
                        <Sparkles className="w-3.5 h-3.5" />
                    </span>
                    <span className="tracking-wide">Ask Sam</span>
                </button>
            )}

            {/* ---- Chat Panel ---- */}
            {open && (
                <div
                    data-testid={TID.chat.panel}
                    className="fixed bottom-4 right-4 z-50 w-[calc(100vw-2rem)] sm:w-[480px] lg:w-[560px] h-[min(760px,calc(100vh-2rem))] bg-white border border-[#0B1F36]/20 flex flex-col shadow-[0_30px_80px_rgba(11,31,54,0.35)]"
                    style={{ borderRadius: 4 }}
                >
                    {/* Header — Deep Navy with a hairline Gold accent */}
                    <div className="relative flex items-center justify-between px-5 py-4 bg-[#0B1F36] text-white">
                        <div className="flex items-center gap-3">
                            <span className="w-8 h-8 inline-flex items-center justify-center bg-[#C8A24A] text-[#0B1F36] rounded-full">
                                <Sparkles className="w-4 h-4" />
                            </span>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.26em] text-[#C8A24A] font-mono-data">
                                    Ask Sam
                                </div>
                                <div className="font-editorial text-[16px] leading-tight mt-0.5">
                                    Your Neighbourhood Advisor
                                </div>
                            </div>
                        </div>
                        <button
                            data-testid={TID.chat.close}
                            onClick={() => setOpen(false)}
                            className="text-white/70 hover:text-white transition-colors"
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 h-px bg-[#C8A24A]/40" />
                    </div>

                    {/* Body */}
                    <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-quiet px-5 py-5 space-y-5 bg-[#FAF9F5]">
                        {showWelcome ? (
                            <WelcomeScreen
                                lead={messages[0]?.lead}
                                starters={starters}
                                onStart={ask}
                                busy={busy}
                            />
                        ) : (
                            messages.map((m, i) => {
                                if (m.kind === 'welcome') return null;
                                const { display, recs, cta } = m.role === 'assistant'
                                    ? parseRecommendations(m.text)
                                    : { display: m.text, recs: [], cta: null };
                                return (
                                    <div key={i} data-testid={TID.chat.message}>
                                        {m.role === 'user' ? (
                                            <div className="text-right">
                                                <div
                                                    className="inline-block max-w-[92%] px-4 py-2.5 bg-[#0B1F36] text-white text-[14px] leading-relaxed"
                                                    style={{ borderRadius: 4 }}
                                                >
                                                    {m.text}
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div
                                                    className="inline-block max-w-full px-4 py-3.5 bg-white border border-neutral-200 text-[14px] leading-[1.65] font-editorial whitespace-pre-line"
                                                    style={{ borderRadius: 4 }}
                                                    data-testid="advisor-body"
                                                >
                                                    {display || (busy && i === messages.length - 1
                                                        ? <span className="opacity-40">Ask Sam is composing…</span>
                                                        : '')}
                                                </div>
                                                {cta && (
                                                    <ContinueWithSam
                                                        cta={cta}
                                                        handoff={handoff}
                                                        sessionId={sessionRef.current}
                                                        onNav={() => setOpen(false)}
                                                        onNotReady={() => setSubscribeOpen(true)}
                                                    />
                                                )}
                                                {recs.length > 0 && (
                                                    <div className="mt-3 grid grid-cols-1 gap-2" data-testid="advisor-recs">
                                                        {recs.map((r, j) => <RecCard key={j} rec={r} onNav={() => setOpen(false)} />)}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}

                        {subscribeOpen && (
                            <SubscribeForm
                                sessionId={sessionRef.current}
                                onClose={() => setSubscribeOpen(false)}
                                onSaved={() => {
                                    setSubscribeOpen(false);
                                    toast.success("You're on the list. Ask Sam will keep the conversation going.");
                                }}
                            />
                        )}
                    </div>

                    {/* Persistent handoff bar — resilient server-driven fallback CTA.
                        Appears whenever the backend confirms the visitor is ready, even
                        if the LLM didn't emit its own cta.show block for this turn. */}
                    {handoff?.ready_for_handoff && !subscribeOpen && (
                        <PersistentHandoffBar
                            handoff={handoff}
                            sessionId={sessionRef.current}
                            onNav={() => setOpen(false)}
                            onNotReady={() => setSubscribeOpen(true)}
                        />
                    )}

                    {/* Composer */}
                    <form
                        onSubmit={(e) => { e.preventDefault(); ask(text); }}
                        className="flex items-center gap-2 border-t border-neutral-200 px-4 py-3 bg-white"
                    >
                        <input
                            data-testid={TID.chat.input}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="Ask about budget, schools, commute, investment…"
                            className="flex-1 border border-neutral-300 px-3.5 py-2.5 text-[14px] focus:border-[#0B1F36] outline-none font-editorial"
                            style={{ borderRadius: 4 }}
                            disabled={busy}
                        />
                        <button
                            data-testid={TID.chat.send}
                            type="submit"
                            disabled={busy || !text.trim()}
                            className="bg-[#0B1F36] text-white px-3.5 py-2.5 hover:bg-[#C8A24A] hover:text-[#0B1F36] transition-colors disabled:opacity-50"
                            style={{ borderRadius: 4 }}
                            aria-label="Send"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </form>

                    {/* Trust footer */}
                    <div className="px-5 py-2.5 bg-white border-t border-neutral-100 text-[10.5px] tracking-[0.06em] text-neutral-500 font-editorial italic text-center">
                        Built from Sam&apos;s local market knowledge and continuously updated neighbourhood data.
                    </div>
                </div>
            )}
        </>
    );
}

// -----------------------------------------------------------------------------
// Welcome screen
// -----------------------------------------------------------------------------
function WelcomeScreen({ lead, starters, onStart, busy }) {
    return (
        <div data-testid="ask-sam-welcome" className="space-y-5">
            <div>
                <div className="text-[10.5px] tracking-[0.24em] uppercase text-[#0B1F36]/60 font-mono-data mb-3">
                    Welcome
                </div>
                <div className="font-editorial text-[22px] leading-[1.25] text-[#0B1F36]">
                    Hello. I&apos;m <span className="italic">Ask Sam.</span>
                </div>
                <p className="mt-4 text-[13.5px] leading-[1.7] text-neutral-700 font-editorial">
                    I&apos;m your AI Neighbourhood Advisor, built from Sam&apos;s local expertise and neighbourhood intelligence.
                </p>
                <p className="mt-3 text-[13.5px] leading-[1.7] text-neutral-700 font-editorial">
                    Whether you&apos;re buying your first home, relocating, investing, selling, or simply exploring communities, I&apos;m here to help you make confident decisions using local market knowledge and data.
                </p>
                {lead && (
                    <p className="mt-3 text-[13px] leading-[1.65] text-[#0B1F36] bg-[#C8A24A]/10 border-l-2 border-[#C8A24A] px-3 py-2.5 font-editorial italic">
                        {lead}
                    </p>
                )}
            </div>

            <div>
                <div className="text-[10.5px] tracking-[0.22em] uppercase text-neutral-500 font-mono-data mb-2.5">
                    I can help you compare
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                    <Chip icon={Home}          label="Neighbourhoods" />
                    <Chip icon={GraduationCap} label="Schools" />
                    <Chip icon={TrainFront}    label="Commute times" />
                    <Chip icon={TrendingUp}    label="Future appreciation" />
                    <Chip icon={DollarSign}    label="Investment opportunities" />
                    <Chip icon={Heart}         label="Lifestyle fit" />
                </div>
            </div>

            <div>
                <div className="text-[10.5px] tracking-[0.22em] uppercase text-neutral-500 font-mono-data mb-2.5">
                    What can I help you discover today?
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                    {starters.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <button
                                key={i}
                                onClick={() => onStart(s.q)}
                                disabled={busy}
                                data-testid={`ask-sam-starter-${i}`}
                                className="text-left flex items-start gap-3 border border-neutral-200 bg-white px-3.5 py-2.5 text-[13.5px] leading-snug font-editorial hover:border-[#0B1F36] hover:bg-[#0B1F36] hover:text-white transition-all duration-200 group"
                                style={{ borderRadius: 4 }}
                            >
                                <Icon className="w-4 h-4 mt-0.5 text-[#0B1F36] group-hover:text-[#C8A24A] shrink-0" />
                                <span>{s.q}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function Chip({ icon: Icon, label }) {
    return (
        <div className="flex items-center gap-2 border border-neutral-200 bg-white px-2.5 py-2 text-[12.5px] text-neutral-700" style={{ borderRadius: 4 }}>
            <Icon className="w-3.5 h-3.5 text-[#C8A24A] shrink-0" />
            <span>{label}</span>
        </div>
    );
}

// -----------------------------------------------------------------------------
// "Continue with Sam" — LLM-driven per-message CTA + profile summary + not-ready link
// -----------------------------------------------------------------------------
function ContinueWithSam({ cta, handoff, sessionId, onNav, onNotReady }) {
    if (!cta || !cta.url) return null;
    const checklist = handoff?.checklist || [];
    const rich = checklist.length >= 3;
    const url = `${cta.url}?advisor_session=${encodeURIComponent(sessionId)}`;
    return (
        <div className="mt-3" data-testid="ask-sam-continue">
            {rich && (
                <div
                    className="mb-2.5 bg-white border border-[#0B1F36]/15 px-4 py-3"
                    style={{ borderRadius: 4 }}
                    data-testid="ask-sam-profile-summary"
                >
                    <div className="text-[10.5px] tracking-[0.24em] uppercase font-mono-data text-[#0B1F36]/70 mb-2">
                        Here&apos;s what I&apos;ve learned so far
                    </div>
                    <ul className="space-y-1">
                        {checklist.map((c) => (
                            <li key={c.key} className="flex items-center gap-2 text-[13px] font-editorial text-[#0B1F36]">
                                <Check className="w-3.5 h-3.5 text-[#C8A24A]" />
                                {c.label}
                            </li>
                        ))}
                    </ul>
                    <p className="mt-3 text-[12.5px] leading-relaxed text-neutral-600 font-editorial italic">
                        If we meet, you won&apos;t have to repeat this. I&apos;ll continue right where we left off.
                    </p>
                </div>
            )}
            <Link
                to={url}
                onClick={onNav}
                data-testid={`ask-sam-continue-${cta.kind || 'buyer'}`}
                className="group block bg-[#0B1F36] text-white border border-[#0B1F36] hover:bg-[#C8A24A] hover:border-[#C8A24A] hover:text-[#0B1F36] transition-all duration-300 px-4 py-3.5"
                style={{ borderRadius: 4 }}
            >
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[10px] font-mono-data tracking-[0.22em] uppercase text-[#C8A24A] group-hover:text-[#0B1F36]/70">
                            Complimentary · 30 min
                        </div>
                        <div className="font-editorial text-[16px] leading-tight mt-1 flex items-center gap-2">
                            <CalendarCheck className="w-4 h-4 shrink-0" />
                            Continue with Sam
                        </div>
                    </div>
                    <ArrowUpRight className="w-4 h-4 shrink-0" />
                </div>
                {cta.note && (
                    <div className="text-[12.5px] mt-2 leading-relaxed text-white/80 group-hover:text-[#0B1F36]/85">
                        {cta.note}
                    </div>
                )}
            </Link>
            <button
                onClick={onNotReady}
                className="mt-2 text-[12px] font-editorial italic text-neutral-500 hover:text-[#0B1F36] transition-colors inline-flex items-center gap-1"
                data-testid="ask-sam-not-ready"
            >
                <Bell className="w-3 h-3" /> Not ready to book? Stay in the loop instead.
            </button>
        </div>
    );
}

// -----------------------------------------------------------------------------
// PersistentHandoffBar — server-driven safety net. Renders as a sticky bar
// above the composer when the backend detects the visitor is ready (>= 3
// signals), regardless of whether the current LLM turn emitted a cta.
// -----------------------------------------------------------------------------
function PersistentHandoffBar({ handoff, sessionId, onNav, onNotReady }) {
    const url = `/strategy-session?advisor_session=${encodeURIComponent(sessionId)}`;
    const count = handoff?.checklist?.length || 0;
    return (
        <div
            className="border-t border-[#C8A24A]/40 bg-[#0B1F36] px-4 py-3 flex items-center justify-between gap-3"
            data-testid="ask-sam-handoff-bar"
        >
            <div className="min-w-0">
                <div className="text-[9.5px] font-mono-data tracking-[0.24em] uppercase text-[#C8A24A]">
                    {count} details captured · Ready when you are
                </div>
                <button
                    onClick={onNotReady}
                    className="text-[11px] font-editorial italic text-white/60 hover:text-white transition-colors inline-flex items-center gap-1 mt-0.5"
                    data-testid="ask-sam-not-ready-persistent"
                >
                    <Bell className="w-3 h-3" /> Not ready? Stay in the loop.
                </button>
            </div>
            <Link
                to={url}
                onClick={onNav}
                data-testid="ask-sam-continue-persistent"
                className="shrink-0 group inline-flex items-center gap-2 bg-[#C8A24A] text-[#0B1F36] px-3.5 py-2 hover:bg-white transition-colors font-editorial text-[13.5px]"
                style={{ borderRadius: 4 }}
            >
                <CalendarCheck className="w-3.5 h-3.5" />
                Continue with Sam
                <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
        </div>
    );
}

// -----------------------------------------------------------------------------
// "Stay in the loop" newsletter capture
// -----------------------------------------------------------------------------
function SubscribeForm({ sessionId, onClose, onSaved }) {
    const [form, setForm] = useState({ first_name: '', email: '', intent: 'buying', cities: '' });
    const [busy, setBusy] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (busy || !form.first_name.trim() || !form.email.trim()) return;
        setBusy(true);
        try {
            const r = await fetch(`${API_BASE}/advisor/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...form, session_id: sessionId }),
            });
            if (!r.ok) throw new Error('failed');
            onSaved();
        } catch (_e) {
            toast.error('Could not save. Please try again.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <form onSubmit={submit} className="bg-white border border-[#0B1F36]/15 px-4 py-4 space-y-2.5" style={{ borderRadius: 4 }} data-testid="ask-sam-subscribe-form">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[10.5px] tracking-[0.24em] uppercase font-mono-data text-[#0B1F36]/70">
                        Stay in the loop
                    </div>
                    <div className="mt-1 text-[13px] font-editorial text-neutral-700 leading-snug">
                        Neighbourhood updates, market reports and new listings tailored to your interests.
                    </div>
                </div>
                <button type="button" onClick={onClose} className="text-neutral-400 hover:text-[#0B1F36]" aria-label="Close">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <input
                required
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                placeholder="First name"
                className="w-full border border-neutral-300 px-3 py-2 text-[13.5px] focus:border-[#0B1F36] outline-none font-editorial"
                style={{ borderRadius: 4 }}
                data-testid="ask-sam-subscribe-name"
            />
            <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Email address"
                className="w-full border border-neutral-300 px-3 py-2 text-[13.5px] focus:border-[#0B1F36] outline-none font-editorial"
                style={{ borderRadius: 4 }}
                data-testid="ask-sam-subscribe-email"
            />
            <select
                value={form.intent}
                onChange={(e) => setForm((f) => ({ ...f, intent: e.target.value }))}
                className="w-full border border-neutral-300 px-3 py-2 text-[13.5px] focus:border-[#0B1F36] outline-none font-editorial bg-white"
                style={{ borderRadius: 4 }}
                data-testid="ask-sam-subscribe-intent"
            >
                <option value="buying">Buying</option>
                <option value="selling">Selling</option>
                <option value="investing">Investing</option>
                <option value="exploring">Just exploring</option>
            </select>
            <input
                value={form.cities}
                onChange={(e) => setForm((f) => ({ ...f, cities: e.target.value }))}
                placeholder="Preferred cities (e.g. Oakville, Ancaster)"
                className="w-full border border-neutral-300 px-3 py-2 text-[13.5px] focus:border-[#0B1F36] outline-none font-editorial"
                style={{ borderRadius: 4 }}
                data-testid="ask-sam-subscribe-cities"
            />
            <button
                type="submit"
                disabled={busy}
                data-testid="ask-sam-subscribe-submit"
                className="w-full bg-[#0B1F36] text-white text-[13.5px] py-2.5 hover:bg-[#C8A24A] hover:text-[#0B1F36] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2 font-editorial"
                style={{ borderRadius: 4 }}
            >
                {busy ? 'Saving…' : (<>Keep me in the loop <ArrowRight className="w-3.5 h-3.5" /></>)}
            </button>
        </form>
    );
}

// -----------------------------------------------------------------------------
// Small recommendation card
// -----------------------------------------------------------------------------
function RecCard({ rec, onNav }) {
    const meta = REC_META[rec.type];
    if (!meta) return null;
    const Icon = meta.icon;
    return (
        <Link
            to={meta.url(rec.slug)}
            onClick={onNav}
            data-testid={`advisor-rec-${rec.type}-${rec.slug}`}
            className="block bg-white border border-neutral-200 px-3.5 py-2.5 hover:border-[#0B1F36] hover:bg-[#0B1F36] hover:text-white transition-all duration-200 group"
            style={{ borderRadius: 4 }}
        >
            <div className="flex items-start gap-3">
                <div className="mt-0.5 shrink-0">
                    <Icon className="w-4 h-4 text-[#0B1F36] group-hover:text-[#C8A24A]" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                        <div className="text-[10px] font-mono-data tracking-widest uppercase text-neutral-500 group-hover:text-white/70">
                            {meta.label}
                        </div>
                        <ArrowUpRight className="w-3.5 h-3.5 text-neutral-400 group-hover:text-white shrink-0" />
                    </div>
                    <div className="font-editorial text-[15px] leading-tight mt-0.5 group-hover:italic">
                        {rec.slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </div>
                    {rec.why && (
                        <div className="text-[12.5px] mt-1 leading-relaxed text-neutral-600 group-hover:text-white/85">
                            {rec.why}
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
}
